/**
 * 저장소 배치 — **선언 한 곳**. 실행이 읽고 남기는 객체와 모델의 빌드 캐시가 어디에 놓이는지는 여기만 안다.
 *
 * ```text
 * <사용자>/<프로젝트>/<모델>/.build/<정체>.fmu             모델 폴더 — 빌드 캐시(내용으로 찾는 컴파일 산출물)
 * <사용자>/<프로젝트>/simulation/<실행>/<순번>/<파일>        결과(어댑터), 짝 덱 baseline.i, 참가자 0 의 run.json
 * <사용자>/<프로젝트>/simulation/<실행>/inputs/<순번>/<이름>  그 참가자가 읽는 입력 — 원래 이름 그대로
 * <사용자>/<프로젝트>/simulation/<실행>/shared/<이름>        공유 설정(precice-config.xml)
 * ```
 *
 * 입력은 `<순번>/inputs/` 가 아니라 `inputs/<순번>/` 에 둔다 — 결과 목록(BFF ListResultFiles)이 `<순번>/` 아래를
 * 재귀로 훑어 마지막 이름만 돌려주므로, 그 아래 입력은 결과로 보이고 복사한 rstplt 가 재시작 원본 판정을 속인다.
 * 결과 자리는 MARS 어댑터가 입력 폴더의 앞 두 조각(`<사용자>/<프로젝트>`)에서 정하고(adapter-core `_upload_dir`), FMU 어댑터는
 * `--results`(프로젝트 폴더 — {@link projectFolderKey})로 받는다.
 * 설계: `docs/DESIGN-run-input-lifecycle.md` §6.2
 *
 * 검출기: `__tests__/runArtifacts.test.ts` 가 이 모듈 밖에서 이 배치의 키를 손으로 짓지 않는지 원본을 훑는다.
 */

/** MinIO 버킷 — 어댑터·엔진이 s3 URI 로 받을 때 필요하다 */
export const RESULT_BUCKET = 'v-smr';

/** 재시작 상태 파일 — 엔진이 쓰고 읽는 이름 그대로. MARS 어댑터는 덱 폴더에서 이 이름을 찾는다 */
export const RESTART_STATE_FILE = 'rstplt';

/**
 * 기준선 덱 — "그 실행이 본 전체 구성".
 *
 * 재시작의 기준선은 원본 모델의 *현재* 상태가 아니라 rstplt 를 만든 그
 * 구성이다. rstplt 는 상태값 덩어리일 뿐 어느 컴포넌트의 값인지는 덱이
 * 정하므로, 둘은 한 짝으로만 뜻을 갖는다.
 * 설계: `docs/DESIGN-restart-baseline-pairing.md`
 */
export const RUN_BASELINE_DECK_FILE = 'baseline.i';
/** 엔진 출력 — 재시작 블록 기록 줄(`domain/mars/output/restartRecords`)이 여기 있다 */
export const RUN_OUTPUT_FILE = 'outdta';
/**
 * 실행 매니페스트 — 시작 시점에 웹이 확정한 **그 실행의 사실**(문제 시각 구간 등). 세션 수준 산출물이라
 * 참가자 0 의 디렉터리에 둔다. 이어보기가 어느 기기에서든 같은 값을 읽고, 실행 도중 모델을 편집·저장해도 바뀌지 않는다.
 */
export const RUN_MANIFEST_FILE = 'run.json';

/** 실행 폴더의 뿌리 — 프로젝트 폴더 안에서 이 이름 아래에 실행마다 한 폴더(BFF 도 같은 이름을 안다) */
export const RUN_ROOT_DIR = 'simulation';
/** 실행 폴더 안의 입력 자리 — 참가자 순번마다 한 폴더 */
const RUN_INPUTS_DIR = 'inputs';
/** 실행 폴더 안의 공유 설정 자리 */
const RUN_SHARED_DIR = 'shared';
/** 모델 폴더 안의 빌드 캐시 자리 */
const BUILD_CACHE_DIR = '.build';

export interface ProjectRef {
  userId: string;
  projectId: string;
}

export interface RunRef extends ProjectRef {
  simulationId: string;
}

export interface RunArtifactRef extends RunRef {
  /** co-sim 참가자 순번 — 단독 실행은 0 */
  taskIndex: number;
  fileName: string;
}

export interface ModelFolderRef extends ProjectRef {
  modelId: string;
}

/** 버킷째의 s3 URI — 어댑터·inputd·엔진처럼 URI 로 받는 소비자용 */
export function objectUri(key: string): string {
  return `s3://${RESULT_BUCKET}/${key}`;
}

/** s3 URI 의 키 — {@link objectUri} 의 역. 이 버킷의 URI 가 아니면 모른다(undefined) */
export function keyOfObjectUri(uri: string): string | undefined {
  const prefix = objectUri('');
  return uri.startsWith(prefix) && uri.length > prefix.length ? uri.slice(prefix.length) : undefined;
}

/** 키가 놓인 프로젝트 — 이 배치의 키는 모두 `<사용자>/<프로젝트>/` 로 시작한다. 조각이 모자라면 모른다(undefined) */
export function projectIdOfKey(key: string): string | undefined {
  const [userId, projectId] = key.split('/');
  return userId && projectId ? projectId : undefined;
}

/** 프로젝트 폴더 — 결과 자리의 뿌리이기도 하다(FMU 어댑터의 `--results`) */
export function projectFolderKey(ref: ProjectRef): string {
  return `${ref.userId}/${ref.projectId}`;
}

/** 실행 폴더의 뿌리 — 프로젝트 폴더 안에서 실행 폴더가 아닌 것(모델·카드 폴더)과 가르는 자리 */
export function runRootFolderKey(ref: ProjectRef): string {
  return `${projectFolderKey(ref)}/${RUN_ROOT_DIR}`;
}

/** 실행 폴더 — 실행 기록을 지우면(BFF) 이 아래가 함께 지워진다 */
export function runFolderKey(ref: RunRef): string {
  return `${runRootFolderKey(ref)}/${ref.simulationId}`;
}

/** 실행 산출물(결과·짝 덱·매니페스트)의 오브젝트 키 */
export function runArtifactKey(ref: RunArtifactRef): string {
  return `${runFolderKey(ref)}/${ref.taskIndex}/${ref.fileName}`;
}

/** 실행 산출물의 s3 URI */
export function runArtifactUri(ref: RunArtifactRef): string {
  return objectUri(runArtifactKey(ref));
}

/** 참가자 한 명의 입력 폴더 */
export function runInputsFolderKey(ref: RunRef & { taskIndex: number }): string {
  return `${runFolderKey(ref)}/${RUN_INPUTS_DIR}/${ref.taskIndex}`;
}

/** 참가자 한 명이 읽는 입력의 키 — 원래 이름 그대로 */
export function runInputKey(ref: RunArtifactRef): string {
  return `${runInputsFolderKey(ref)}/${ref.fileName}`;
}

/** 공유 설정의 키 — 이름이 SM ConfigMap 의 키다 */
export function runSharedKey(ref: RunRef & { fileName: string }): string {
  return `${runFolderKey(ref)}/${RUN_SHARED_DIR}/${ref.fileName}`;
}

/** 모델 폴더 — 모델을 지우면 이 아래가 함께 지워진다 */
export function modelFolderKey(ref: ModelFolderRef): string {
  return `${projectFolderKey(ref)}/${ref.modelId}`;
}

/** 모델 폴더의 파일 — 퀵 실행 카드가 올린 로컬 바이너리(.fmu) 등 */
export function modelFileKey(ref: ModelFolderRef & { fileName: string }): string {
  return `${modelFolderKey(ref)}/${ref.fileName}`;
}

/** 빌드 캐시 폴더 — 모델당 한 정체만 남긴다(`app/authoring/fmuBuildCache`) */
export function buildCacheFolderKey(ref: ModelFolderRef): string {
  return `${modelFolderKey(ref)}/${BUILD_CACHE_DIR}`;
}

/** 빌드 캐시의 객체 — 이름이 곧 컴파일의 정체다 */
export function buildCacheKey(ref: ModelFolderRef & { identity: string }): string {
  return `${buildCacheFolderKey(ref)}/${ref.identity}.fmu`;
}
